import { CollectionGroup, CollectionNavigationItem } from '../../shared/components/session-collection/session-collection.models';
import { ParentAuthService } from '../parent-auth.service';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { switchMap } from 'rxjs';
import { PatientTargetModel, PromptScheduleModel, PatientBehaviorModel, PatientBehaviorType, PatientPlaylistModel, PatientProgramModel, PatientModel } from '../../core/api/models';
import { ParentApiService } from '../../core/api/services';
import { PatientTargetForChainModel } from '../../shared/models/models';
import { Utils } from '../../core/utils';
import { AlertService } from '../../shared/services/alert.service';
import { SESSION_DATA_SOURCE } from '../../core/services/session-data-source';
import { PARENT_TARGET_DETAILS } from '../../staff/home/target-details/target-details.component';
import { ParentSessionService } from './parent-session.service';

@Component({
  selector: 'app-parent-collect',
  templateUrl: './parent-collect.component.html',
  styleUrls: ['./parent-collect.component.css', '../../staff/home/staff-home.component.css'],
  standalone: false,
  providers: [
    { provide: SESSION_DATA_SOURCE, useExisting: ParentSessionService },
    { provide: PARENT_TARGET_DETAILS, useValue: true },
  ],
})
export class ParentCollectComponent implements OnInit {
  patients: PatientModel[] = [];
  patientID = 0;
  behaviorType = PatientBehaviorType;
  behaviors: PatientBehaviorModel[] = [];
  playlists: PatientPlaylistModel[] = [];
  selectedPlaylist: PatientPlaylistModel | null = null;
  programs: PatientProgramModel[] = [];
  selectedProgram: PatientProgramModel | null = null;

  get showingTargets(): PatientTargetModel[] {
    return this.targets.filter(target => (!this.selectedProgram || target.PatientProgramIDs?.includes(this.selectedProgram.ID!))
      && (!this.selectedPlaylist || this.selectedPlaylist.Items?.some(item => item.PatientTargetID === target.ID
        || (item.PatientProgramID != null && target.PatientProgramIDs?.includes(item.PatientProgramID)))));
  }

  get collectionGroups(): CollectionGroup[] {
    const targets = this.showingTargets;
    return targets.length ? [{Title: '', items: [{Title: '', Name: this.selectedProgram?.ProgramName ?? '', ProgramID: this.selectedProgram?.ID, Targets: targets}]}] : [];
  }

  get collectionNavigation(): CollectionNavigationItem[] {
    return this.playlists.length || this.programs.length ? [
      {kind: 'all', id: 0, name: 'All targets', selected: !this.selectedPlaylist && !this.selectedProgram},
      ...this.programs.map(program => ({kind: 'program' as const, id: program.ID ?? 0, name: program.ProgramName ?? '', selected: program.ID === this.selectedProgram?.ID})),
      ...this.playlists.map(playlist => ({kind: 'playlist' as const, id: playlist.ID ?? 0, name: playlist.PlaylistName ?? '', selected: playlist.ID === this.selectedPlaylist?.ID}))
    ] : [];
  }

  selectCollectionItem(item: CollectionNavigationItem): void {
    this.selectedPlaylist = item.kind === 'playlist' ? this.playlists.find(x => x.ID === item.id) ?? null : null;
    this.selectedProgram = item.kind === 'program' ? this.programs.find(x => x.ID === item.id) ?? null : null;
  }

  get timerString(): string {
    const start = this.parentSessionService.getPatientSession()?.StartedDate;
    return start ? 'Started ' + new Date(start).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}) : '';
  }

  patientName: string = '';
  targets: PatientTargetModel[] = [];
  promptSchedules: PromptScheduleModel[] = [];
  subTargetsDictionary: { [parentTargetID: number]: PatientTargetForChainModel[] } = {};
  loading: boolean = true;
  finishing: boolean = false;
  errorMessage: string = '';

  constructor(
    private parentAuth: ParentAuthService,
    private route: ActivatedRoute,
    private router: Router,
    private parentApiService: ParentApiService,
    private alertService: AlertService,
    public parentSessionService: ParentSessionService
  ) { }

  ngOnInit(): void {
    const patientID = Number(this.route.snapshot.paramMap.get('patientID'));

    this.patientID = patientID;
    this.parentSessionService.loadCollectionData(patientID).pipe(
      switchMap(({ patient, targets, promptSchedules, behaviors, playlists, programs, options }) => {
        this.patients = JSON.parse(localStorage.getItem('parentPatients:' + this.parentAuth.storageScope) ?? '[]');
        this.behaviors = behaviors;
        this.playlists = playlists;
        this.programs = programs ?? [];
        this.patientName = patient.PatientName ?? '';
        this.promptSchedules = promptSchedules.filter((p) => !p.IsDeleted);
        const subTargetIDs = new Set(targets.flatMap((t) => t.SubTargets ?? []).map((s) => s.ID ?? 0));
        for (const target of targets) {
          if (target.SubTargets?.length) {
            this.subTargetsDictionary[target.ID ?? 0] = target.SubTargets
              .map((subTarget) => {
                const foundTarget = targets.find((t) => t.ID === subTarget.ID);
                return foundTarget ? { ...foundTarget, ForceOpen: subTarget.ForceOpen } : null;
              })
              .filter((t) => t !== null) as PatientTargetForChainModel[];
          }
        }
        this.targets = targets.filter((t) => !subTargetIDs.has(t.ID ?? 0));
        return this.parentSessionService.startSession(patientID, targets, behaviors, options);
      })
    ).subscribe({
      next: () => {
        this.loading = false;
      },
      error: (error) => {
        console.error('Error starting parent session', error);
        this.errorMessage = Utils.getErrorMessageFromErrorResponse(error);
        this.loading = false;
      }
    });
  }

  switchPatient(patient: PatientModel): void {
    this.router.navigate(['/parent/home'], { queryParams: { patientID: patient.ID } });
  }

  finish(): void {
    if (this.parentSessionService.hasRunningTimers()) {
      this.alertService.info("Stop or complete the running timers before ending the session.");
      return;
    }
    this.finishing = true;
    this.parentSessionService.finishSession().subscribe((synced) => {
      if (!synced) {
        this.alertService.info("Saved on this device. It will be sent when you're back online.");
      }
      this.router.navigate(['/parent/home']);
    });
  }
}
